// Hook for conflict detection
// Provides real-time conflict detection for task assignments with caching
// and automatic refresh capabilities.
// CHANGE: Added request ID pattern to prevent stale async responses from overwriting current state
// CHANGE: Removed all debug console statements for production
// PERF: Accepts preloadedTasks to avoid re-fetching data already loaded by the execution layer

import { useState, useEffect, useCallback, useRef } from 'react';
import { detectConflicts, ignoreConflict, unignoreConflict, TaskConflict, PreloadedTaskForConflict } from '../lib/conflictDetection';

export function useConflictDetection(
  siteId: string | null,
  date: string | null,
  preloadedTasks?: PreloadedTaskForConflict[]
) {
  const [conflicts, setConflicts] = useState<TaskConflict[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const loadConflicts = useCallback(async (tasksOverride?: PreloadedTaskForConflict[]) => {
    if (!siteId || !date) {
      setConflicts([]);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const tasksToUse = tasksOverride ?? preloadedTasks;
      const detectedConflicts = await detectConflicts(siteId, date, tasksToUse);
      if (requestId !== requestIdRef.current) return;
      setConflicts(detectedConflicts);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [siteId, date, preloadedTasks]);

  useEffect(() => {
    loadConflicts();
  }, [loadConflicts]);

  const handleIgnoreConflict = useCallback(
    async (
      taskId1: string,
      taskId2: string,
      userId: string,
      conflictDate: string
    ) => {
      if (!siteId) return;

      try {
        await ignoreConflict(siteId, taskId1, taskId2, userId, conflictDate);
        await loadConflicts();
      } catch (err) {
        throw err;
      }
    },
    [siteId, loadConflicts]
  );

  const handleUnignoreConflict = useCallback(
    async (
      taskId1: string,
      taskId2: string,
      userId: string,
      conflictDate: string
    ) => {
      if (!siteId) return;

      try {
        await unignoreConflict(siteId, taskId1, taskId2, userId, conflictDate);
        await loadConflicts();
      } catch (err) {
        throw err;
      }
    },
    [siteId, loadConflicts]
  );

  const activeConflicts = conflicts.filter(c => !c.isIgnored);
  const ignoredConflicts = conflicts.filter(c => c.isIgnored);

  return {
    conflicts,
    activeConflicts,
    ignoredConflicts,
    loading,
    error,
    refresh: loadConflicts,
    ignoreConflict: handleIgnoreConflict,
    unignoreConflict: handleUnignoreConflict
  };
}
